import { getCoreRollCandidates, hasCoreAttackStyle } from './coreAttackStyles';
import { acquireShards, getTotalShardsAcquired } from './shardTotals';
import { getRelicPrice, prepareShop } from './shop';
import { getRoomBoardCondition } from './boardConditions';
import { createDoor, generateDoorOffers } from './doors';
import {
  generatePortalRoutes,
  HARD_PORTAL_COST,
  isStarterPortalChamber,
} from './portalRoutes';
import { BOSS_MAX_HP } from './corePhases';
import {
  DOOR_BOARD_VERSION,
  migratePlayerBoardBalance,
  BREACH_BOARD_VERSION,
  generateDoorBoard,
  generateBreachBoard,
  getBoardSquare,
  isNegativeBoardSquare,
} from './doorBoard';
import { createEmptyPortalPair, generatePortalPairForRoom } from './portalPair';
import { RELICS, RELIC_CHEST_WEIGHTS, isBossRelic } from './relics';
import {
  LAST_STAND_TARGET,
  canTriggerLastStand,
  didLastStandSurvive,
  getLastStandRollGame,
  getLastStandEncounter,
  requestLastStandRoll,
} from './lastStandProtocol';
import {
  commitChainReaction,
  getBoardEffectTotal,
  getBoardRollSteps,
  getChainReactionActivation,
  getTriggeredBoardSquares,
  isDieFrozen,
  getEnemyAttackCount,
} from './chainReaction';
import { getBountyTagOutcome, startBountyTagThrow } from './bountyTag';
import { resolveEnemyAttacks } from './enemyAttacks';
import { getThrowCombo, startThrowCombo } from './throwCombo';
import {
  consumeRelicHealingEffects,
  getRelicBlockEffects,
  getRelicHealingEffects,
  getRelicShardEffects,
  relicConditionMatches as rollConditionMatches,
} from './relicEffects';
import { drawRelicFromPool, isChestRelic, isShopRelic } from './relicDrops';
import { DEFAULT_DIE, formatDieValue, isValidDieRoll } from './dice';
import {
  getBoardRelaunchRequest,
  getPowerUpMultiplier,
  getPowerUpMultipliers,
  getPowerUpNaturalRoll,
  isValidPowerUpFaces,
  boostPowerUpRoll,
  getPowerUpBonusPercent,
  upgradePowerUpTile,
} from './powerUp';
import {
  chooseBoardTileEffect,
  needsBoardTileChoice,
  prepareBoardTileEffects,
} from './boardTileEffects';
import { createBossUpgradeRolls } from './bossUpgradeRolls';
import {
  getSectorMinion,
  getMiniBossRoom,
  getMiniBossIntent,
  MINI_BOSS_HP,
  MINI_BOSS_SECTOR,
  MINI_BOSS_SHARDS,
  RIFT_WARDEN,
  createGuardian,
  getGuardianCounterDamage,
  GUARDIAN_PHASES,
} from './sectorEnemies';
import { CORE_SECTOR, REGULAR_SECTORS, TOTAL_SECTORS } from './runConstants';
import {
  FIRST_STRIKE_SHARDS,
  getRepairCost,
  getSectorRepairCount,
  REPAIR_HEAL,
} from './economy';

export const MAX_HP = 10;
export { getRepairCost } from './economy';

export const getMaxHp = (game) =>
  Math.max(MAX_HP, game.maxHp || 0, game.hp || 0);

// Healing fills missing health first; any overflow permanently grows this run's cap.
export const applyHealing = (game, amount) => {
  const hp = game.hp + Math.max(0, amount);
  return { hp, maxHp: Math.max(getMaxHp(game), hp) };
};

// Show tile healing as soon as the landing is committed. Combat still resolves
// from its saved starting health, so this preview never grants healing twice.
export const getLandingHealingDisplay = (game) => {
  if (
    ![
      'rolled',
      'guardianRolled',
      'minibossRolled',
      'bossPlayerRolled',
      'bossRolled',
    ].includes(game.phase) ||
    !Number.isInteger(game.landingIndex) ||
    game.lastStandProtocol?.pending ||
    game.bossRoundState?.playerHealingApplied
  )
    return null;
  const amount = getBoardEffectTotal(game, 'heal');
  return amount > 0 ? applyHealing(game, amount) : null;
};

export { RELICS } from './relics';

const RELIC_OFFER_COUNT = 4;
export const RELIC_REROLL_COST = 1;

export const BREACH_ROOM = {
  id: 'breach',
  name: 'The Breach',
  kind: 'Salvage',
  damage: 0,
  shards: 0,
  heal: 0,
  description:
    'A signal cuts through the fracture. Send your cube across the board and power a way out.',
};

const getAvailableRelics = (game, owned = game.relics || []) =>
  Object.keys(RELICS).filter(
    (id) =>
      !RELICS[id].deprecated &&
      !RELICS[id].bossOnly &&
      !owned.includes(id) &&
      (game.floor < CORE_SECTOR || isBossRelic(id, game))
  );

export const drawTreasureRelic = (game, random = Math.random) => {
  const unowned = getAvailableRelics(game).filter(isChestRelic);
  const available = unowned.length
    ? unowned
    : getAvailableRelics(game, []).filter(
        (id) =>
          isChestRelic(id) &&
          !(id === 'mirrorChip' && game.relics?.includes(id))
      );
  const id = drawRelicFromPool(available, RELIC_CHEST_WEIGHTS, random);
  return { id, rarity: RELICS[id]?.rarity || 'Common' };
};

export const getBoardTreasureRewards = (game) =>
  Array.isArray(game.boardTreasureRewards) && game.boardTreasureRewards.length
    ? game.boardTreasureRewards
    : game.boardTreasureReward
    ? [game.boardTreasureReward]
    : [];

const claimBoardTreasures = (game, random = Math.random) => {
  const relics = [...game.relics];
  const rewards = getTriggeredBoardSquares(game)
    .filter(({ square }) => square.effect?.type === 'relic')
    .map(({ index }) => {
      const reward = {
        ...drawTreasureRelic({ ...game, relics }, random),
        index,
      };
      relics.push(reward.id);
      return reward;
    });
  return {
    relics,
    boardTreasureReward: rewards[0] || null,
    boardTreasureRewards: rewards,
  };
};

// Resolve choices before awarding redirected chests or calculating any
// relic bonuses. The saved completion flag makes rewards reload-safe.
export const finishBoardTileEffects = (game, random = Math.random) => {
  const prepared = prepareBoardTileEffects(game);
  const state = prepared.boardTileResolution;
  if (!state || state.pending || state.finalized) return prepared;
  const treasure = claimBoardTreasures(prepared, random);
  return {
    ...prepared,
    ...treasure,
    boardTileResolution: { ...state, finalized: true },
    ...(prepared.floor === CORE_SECTOR
      ? {
          bossUpgradeRolls: {
            ...prepared.bossUpgradeRolls,
            ...createBossUpgradeRolls(
              {
                relics: treasure.relics.filter(
                  (id) => !prepared.bossUpgradeRolls?.[id]
                ),
              },
              random
            ),
          },
        }
      : {}),
  };
};

export const resolveBoardTileChoice = (game, choice, random = Math.random) => {
  const next = chooseBoardTileEffect(game, choice, random);
  return next === game ? game : finishBoardTileEffects(next, random);
};

export const completeBoardRelaunch = (
  game,
  landingIndex,
  requestId,
  random = Math.random,
  faceValues = null,
  settledRoll = null
) => {
  const request = getBoardRelaunchRequest(game);
  const landing = getBoardSquare(game.doorBoard, landingIndex);
  if (
    !['rolled', 'bossPlayerRolled', 'bossRolled'].includes(game.phase) ||
    !request ||
    request.id !== requestId ||
    !landing ||
    game.bossRoundState?.attack
  )
    return game;
  const multipliers = [
    ...getPowerUpMultipliers(game.powerUp),
    ...(request.type === 'powerUp' ? [request.multiplier || 1] : []),
  ];
  const finalRoll = settledRoll ?? request.after;
  const naturalRoll = getPowerUpNaturalRoll(
    finalRoll,
    game.selectedDie,
    { multipliers },
    hasCoreAttackStyle(game) ? getCoreRollCandidates(game) : undefined
  );
  if (
    naturalRoll === null ||
    (isValidPowerUpFaces(faceValues) && !faceValues.includes(finalRoll))
  )
    return game;
  const state = game.boardTileResolution;
  if (request.type === 'gambit') {
    const logs = [
      ...state.logs,
      `Gambit: ${request.before} → ${finalRoll}. Spent 1 shard; new landing locked.`,
    ];
    const relaunched = prepareBoardTileEffects(
      commitChainReaction(
        {
          ...game,
          roll: finalRoll,
          powerUp: game.powerUp
            ? {
                ...game.powerUp,
                naturalRoll,
                gambitUsed: true,
                faceValues: isValidPowerUpFaces(faceValues)
                  ? [...faceValues]
                  : undefined,
                logs,
                interference: state.interference,
              }
            : null,
          landingIndex,
          boardModifier: landing.modifier || 0,
          boardTileResolution: null,
          chainReactionActivation: null,
          boardTreasureReward: null,
          boardTreasureRewards: [],
          flipped: false,
        },
        landingIndex
      ),
      {
        gambitUsed: true,
        logs,
        interference: state.interference,
      }
    );
    return finishBoardTileEffects(relaunched, random);
  }
  const rebuiltTile = upgradePowerUpTile(game.doorBoard[request.index]);
  const rebuiltMultiplier = getPowerUpMultiplier(rebuiltTile.effect);
  return finishBoardTileEffects(
    prepareBoardTileEffects(
      commitChainReaction(
        {
          ...game,
          roll: finalRoll,
          powerUp: {
            naturalRoll,
            boosts: (game.powerUp?.boosts || 0) + 1,
            ...(isValidPowerUpFaces(faceValues)
              ? { faceValues: [...faceValues] }
              : isValidPowerUpFaces(game.powerUp?.faceValues)
              ? {
                  faceValues: game.powerUp.faceValues.map((value) =>
                    boostPowerUpRoll(value, request.multiplier)
                  ),
                }
              : {}),
            multipliers,
            upgradedIndices: [
              ...(game.powerUp?.upgradedIndices ||
                game.powerUp?.destroyedIndices ||
                []),
              request.index,
            ],
            gambitUsed: state.gambitUsed,
            interference: state.interference,
            logs: [
              ...state.logs,
              `POWER UP${
                request.multiplier > 1 ? ` ×${request.multiplier}` : ''
              }: faces boosted +${getPowerUpBonusPercent(
                request.multiplier
              )}%; landed ${finalRoll}. Tile rebuilt as POWER UP ×${rebuiltMultiplier} (+${getPowerUpBonusPercent(
                rebuiltMultiplier
              )}%); die relaunched.`,
            ],
          },
          doorBoard: game.doorBoard.map((square, index) =>
            index === request.index ? rebuiltTile : square
          ),
          landingIndex,
          boardModifier:
            landingIndex === request.index ? 0 : landing.modifier || 0,
          boardTileResolution: null,
          chainReactionActivation: null,
          flipped: false,
        },
        landingIndex
      ),
      {
        carriedEntries: state.entries.filter((entry) => entry.done),
      }
    ),
    random
  );
};

const validRelicOffers = (game) =>
  Array.isArray(game.relicOffers) &&
  (!(game.portalRun && game.floor === 2 && game.phase === 'choose') ||
    game.relicOffers.some((id) => RELICS[id]?.category === 'Common')) &&
  game.relicOffers.length === RELIC_OFFER_COUNT &&
  new Set(game.relicOffers).size === RELIC_OFFER_COUNT &&
  game.relicOffers.every(
    (id) =>
      isShopRelic(id) &&
      !RELICS[id].bossOnly &&
      !(game.relics || []).includes(id)
  );

export const generateRelicOptions = (game, random = Math.random) => {
  const available = getAvailableRelics(game).filter(isShopRelic);
  const options = [];

  while (options.length < RELIC_OFFER_COUNT && available.length > 0) {
    const id = drawRelicFromPool(available, undefined, random);
    if (!id) break;
    options.push(id);
    available.splice(available.indexOf(id), 1);
  }

  // This only matters for a future catalog smaller than six, but guarantees
  // the UI contract without ever duplicating a relic card.
  if (options.length < RELIC_OFFER_COUNT) {
    available.forEach((id) => {
      if (options.length < RELIC_OFFER_COUNT) options.push(id);
    });
  }
  return options;
};

export const generateMarketOptions = (game, random = Math.random) => {
  const available = getAvailableRelics(game).filter(isShopRelic);
  const guaranteed = ['Common', 'Uncommon']
    .map((rarity) => {
      const pool = available.filter((id) => RELICS[id].category === rarity);
      return pool[
        Math.min(pool.length - 1, Math.floor(random() * pool.length))
      ];
    })
    .filter(Boolean);
  const remaining = generateRelicOptions(
    { ...game, relics: [...game.relics, ...guaranteed] },
    random
  );
  return [...guaranteed, ...remaining].slice(0, RELIC_OFFER_COUNT);
};

export const prepareRelicOffers = (game, random = Math.random) => {
  if (game.phase === 'relic' && !validRelicOffers(game)) {
    return { ...game, relicOffers: generateRelicOptions(game, random) };
  }
  if (
    game.phase === 'choose' &&
    game.portalRun &&
    game.relicOffers &&
    game.marketPurchased !== game.floor &&
    !validRelicOffers(game)
  ) {
    return { ...game, relicOffers: generateMarketOptions(game, random) };
  }
  return game;
};

export const FLOORS = [
  [
    {
      id: 'f1-safe',
      name: 'Flickering Infirmary',
      kind: 'Recovery',
      target: 38,
      damage: 2,
      shards: 1,
      heal: 2,
      description: 'Find a working med station among the sparks.',
    },
    {
      id: 'f1-risk',
      name: 'Data Vault',
      kind: 'Treasure',
      target: 60,
      damage: 3,
      shards: 3,
      heal: 0,
      description: 'Crack the vault before its security wakes.',
    },
  ],
  [
    {
      id: 'f2-safe',
      name: 'Cooling Chamber',
      kind: 'Recovery',
      target: 44,
      damage: 2,
      shards: 1,
      heal: 2,
      description: 'Rest inside an unstable cooling field.',
    },
    {
      id: 'f2-risk',
      name: 'Laser Gallery',
      kind: 'Treasure',
      target: 65,
      damage: 3,
      shards: 3,
      heal: 0,
      description: 'Slip through the beams for a cache of shards.',
    },
  ],
  [
    {
      id: 'f3-safe',
      name: 'Broken Clinic',
      kind: 'Recovery',
      target: 49,
      damage: 3,
      shards: 1,
      heal: 2,
      description: 'Salvage supplies from the abandoned clinic.',
    },
    {
      id: 'f3-risk',
      name: 'The Mimic Cache',
      kind: 'Treasure',
      target: 70,
      damage: 4,
      shards: 3,
      heal: 0,
      description: 'Open a chest that might open you first.',
    },
  ],
  [
    {
      id: 'f4-safe',
      name: 'Last Safe Signal',
      kind: 'Recovery',
      target: 54,
      damage: 3,
      shards: 1,
      heal: 3,
      description: 'Follow a faint signal to one last refuge.',
    },
    {
      id: 'f4-risk',
      name: 'The Crown Circuit',
      kind: 'Treasure',
      target: 75,
      damage: 5,
      shards: 3,
      heal: 0,
      description: 'Steal power from the Core itself.',
    },
  ],
  // Temporary sector 5–9 room pairs use existing encounters and reward rules.
  // Keep these pairings easy to swap as the later-sector concepts evolve.
  ...[
    ['salvage-5', 'trial-2'],
    ['recovery-6', 'anomaly-4'],
    ['supply-5', 'armory-6'],
    ['sanctuary-7', 'trial-8'],
    ['treasure-8', 'anomaly-10'],
  ].map((encounters, index) =>
    encounters.map((id) => createDoor(id, index + 5))
  ),
  [
    {
      id: 'boss',
      name: 'The Glitch Core',
      kind: 'Boss',
      target: 72,
      damage: 4,
      shards: 0,
      heal: 0,
      description:
        'Survive its attacks and chip away at its HP across multiple rolls.',
    },
  ],
];

export const createGame = (progress = {}) => ({
  phase: 'intro',
  guardianCombatVersion: 1,
  guardian: null,
  miniBoss: null,
  portalRun: false,
  breachIntro: false,
  portalRoutes: null,
  marketPurchased: null,
  marketPurchase: null,
  floor: 1,
  hp: MAX_HP,
  maxHp: MAX_HP,
  shards: 0,
  totalShardsAcquired: 0,
  relics: [],
  relicHealingUses: {},
  repairPurchases: null,
  roomId: null,
  conditionSeed: Math.floor(Math.random() * 0x100000000),
  boardCondition: null,
  selectedDie: DEFAULT_DIE,
  roll: null,
  flipped: false,
  floorFlipUsed: false,
  phaseAnchor: { armed: false, usedFloor: null },
  chainReaction: { usedFloor: null, armed: false },
  chainReactionActivation: null,
  bountyTag: { targetIndex: null, attempt: null },
  throwCombo: null,
  portalPair: { entryIndex: null, exitIndex: null, used: false },
  ghostBump: { armed: false, used: false, throwRound: null },
  decoyBeacon: { deployed: false, used: false, throwRound: null },
  lastStandProtocol: { used: false, pending: null, lastAttempt: null },
  lastResult: null,
  history: [],
  bestScore: progress.bestScore || 0,
  bestAttack: progress.bestAttack || 0,
  victories: progress.victories || 0,
  score: 0,
  runBestAttack: 0,
  newBestScore: false,
  bossHp: BOSS_MAX_HP,
  corePhase: null,
  bossRound: 1,
  bossRoundState: null,
  coreShield: 0,
  bossRoll: null,
  bossMassiveAttackDamage: null,
  bossLandingIndex: null,
  bossBoardModifier: 0,
  stance: 'strike',
  boardTileResolution: null,
  powerUp: null,
  coreStyleVersion: null,
  coreDieFaces: null,
  coreDieCurrentFaces: null,
  shopOffers: null,
  relicOffers: null,
  doorOffers: null,
  doorBoardVersion: null,
  boardTreasureReward: null,
  boardTreasureRewards: [],
});

const getProgressScoreBreakdown = ({ floor = 1, shards = 0, relics = [] }) => {
  const ownedRelics = new Set(relics).size;
  const completedSectors = Math.max(0, floor - 1);
  return [
    { label: 'Run launched', points: 50 },
    {
      label: 'Sectors completed',
      detail: `${completedSectors} × 100`,
      points: completedSectors * 100,
    },
    {
      label: 'Relics collected',
      detail: `${ownedRelics} × 75`,
      points: ownedRelics * 75,
    },
    {
      label: 'Shards carried',
      detail: `${shards} × 25`,
      points: shards * 25,
    },
  ];
};

export const getDefeatScore = ({
  enemyMaxHp = 0,
  enemyHp = 0,
  ...progress
}) => {
  const damage = Math.max(0, enemyMaxHp - Math.max(0, enemyHp));
  const scoreBreakdown = getProgressScoreBreakdown(progress);
  if (damage > 0)
    scoreBreakdown.push({
      label: 'Final encounter damage',
      detail: `${damage} damage / 4, rounded up`,
      points: Math.ceil(damage / 4),
    });
  return {
    score: scoreBreakdown.reduce((total, item) => total + item.points, 0),
    scoreBreakdown,
  };
};

export const getVictoryScore = ({ hp, shards, relics, bossRound }) => {
  const speedBonus = Math.max(0, 500 - (bossRound || 0) * 50);
  const scoreBreakdown = [
    ...getProgressScoreBreakdown({ floor: TOTAL_SECTORS, shards, relics }),
    { label: 'Core defeated', points: 500 },
    { label: 'HP remaining', detail: `${hp} × 25`, points: hp * 25 },
    {
      label: 'Turns to defeat',
      detail: `${bossRound} turns · 500 minus 50 per turn, minimum 0`,
      points: speedBonus,
    },
  ];

  return {
    score: scoreBreakdown.reduce((total, item) => total + item.points, 0),
    scoreBreakdown,
  };
};

export const getRecentRooms = (history) => {
  let coreSeen = false;
  return history
    .filter((entry) => {
      if (!entry || typeof entry.roomName !== 'string') return false;
      if (!entry.boss && entry.roomName !== 'The Glitch Core') return true;
      if (coreSeen) return false;
      coreSeen = true;
      return true;
    })
    .slice(0, 5);
};

export const getRooms = (game) => {
  if (game.floor === CORE_SECTOR) return FLOORS[CORE_SECTOR - 1];
  if (game.breachIntro && game.floor === 1) return [BREACH_ROOM];
  const offers = game.doorOffers?.[game.floor];
  if (Array.isArray(offers)) {
    const ids =
      game.portalRun &&
      game.portalRoutes?.floor === game.floor &&
      (game.portalRoutes.hardUnlocked || isStarterPortalChamber(game))
        ? [...offers, game.portalRoutes.hard]
        : offers;
    return ids
      .map(
        (id) =>
          createDoor(id, game.floor) ||
          FLOORS[game.floor - 1]?.find((room) => room.id === id)
      )
      .filter(Boolean);
  }
  // Preserve encounters in saves made before random doors were introduced.
  return FLOORS[game.floor - 1] || [];
};

export const prepareDoors = (game) => {
  if (
    game.doorOffers &&
    Array.from({ length: REGULAR_SECTORS }, (_, index) => index + 1).every(
      (floor) => Array.isArray(game.doorOffers[floor])
    )
  )
    return game;
  const generated = generateDoorOffers();
  const doorOffers = { ...generated, ...(game.doorOffers || {}) };
  if (
    !game.portalRun &&
    !game.doorOffers?.[game.floor] &&
    game.floor <= REGULAR_SECTORS &&
    ['ready', 'rolled', 'result', 'lost', ...GUARDIAN_PHASES].includes(
      game.phase
    )
  ) {
    doorOffers[game.floor] = FLOORS[game.floor - 1].map(({ id }) => id);
  }
  return { ...game, doorOffers };
};

export const prepareDoorBoard = (game) => {
  game = migratePlayerBoardBalance(game);
  return game.floor < CORE_SECTOR &&
    ['ready', 'rolled'].includes(game.phase) &&
    (!Array.isArray(game.doorBoard) ||
      game.doorBoard.length !== 25 ||
      (game.phase === 'ready' &&
        game.doorBoardVersion !==
          (game.breachIntro ? BREACH_BOARD_VERSION : DOOR_BOARD_VERSION)))
    ? {
        ...game,
        doorBoard: game.breachIntro
          ? generateBreachBoard()
          : generateDoorBoard(Math.random, { floor: game.floor }),
        portalPair: generatePortalPairForRoom(game, game.roomId),
        doorBoardVersion: game.breachIntro
          ? BREACH_BOARD_VERSION
          : DOOR_BOARD_VERSION,
      }
    : game;
};

export const validDoorOffers = (game) =>
  !!game.doorOffers &&
  Object.entries(game.doorOffers).length > 0 &&
  Object.entries(game.doorOffers).every(([sector, ids]) => {
    const floor = Number(sector);
    if (
      !Number.isInteger(floor) ||
      floor < 1 ||
      floor > REGULAR_SECTORS ||
      !Array.isArray(ids) ||
      ids.length !== 2
    )
      return false;
    const rooms = ids
      .map(
        (id) =>
          createDoor(id, floor) ||
          FLOORS[floor - 1]?.find((room) => room.id === id)
      )
      .filter(Boolean);
    return (
      rooms.length === 2 && new Set(rooms.map(({ kind }) => kind)).size === 2
    );
  });

export const getRelicOptions = (game) => {
  if (validRelicOffers(game)) return game.relicOffers;
  return generateRelicOptions(game);
};

export const rerollRelicOptions = (game, random = Math.random) => {
  if (game.phase !== 'relic' || game.shards < RELIC_REROLL_COST) return game;

  const currentOffers = getRelicOptions(game);
  const optionsWithoutCurrentOffers = generateRelicOptions(
    {
      ...game,
      relics: [...new Set([...(game.relics || []), ...currentOffers])],
    },
    random
  );
  const fallbackOptions =
    optionsWithoutCurrentOffers.length < RELIC_OFFER_COUNT
      ? generateRelicOptions(game, random).filter(
          (id) => !optionsWithoutCurrentOffers.includes(id)
        )
      : [];
  const relicOffers = [
    ...optionsWithoutCurrentOffers,
    ...fallbackOptions,
  ].slice(0, RELIC_OFFER_COUNT);

  return {
    ...game,
    shards: game.shards - RELIC_REROLL_COST,
    totalShardsAcquired: getTotalShardsAcquired(game),
    relicOffers,
  };
};

export const startRun = (game) => {
  const baseGame = createGame(game);
  return {
    ...baseGame,
    phase: 'opening',
    portalRun: true,
    breachIntro: true,
    doorOffers: generateDoorOffers(),
  };
};

export const enterBreach = (game) =>
  game.phase !== 'opening'
    ? game
    : {
        ...game,
        phase: 'ready',
        roomId: BREACH_ROOM.id,
        boardCondition: null,
        doorBoard: generateBreachBoard(),
        portalPair: generatePortalPairForRoom(game, BREACH_ROOM.id),
        doorBoardVersion: BREACH_BOARD_VERSION,
      };

export const chooseRelic = (game, id) => {
  if (!isShopRelic(id)) return game;
  if (game.portalRun) {
    if (
      game.phase !== 'choose' ||
      !RELICS[id] ||
      RELICS[id].bossOnly ||
      !game.relicOffers?.includes(id) ||
      game.relics.includes(id) ||
      game.marketPurchased === game.floor ||
      game.shards < getRelicPrice(game, id)
    )
      return game;
    return {
      ...game,
      shards: game.shards - getRelicPrice(game, id),
      totalShardsAcquired: getTotalShardsAcquired(game),
      relics: [...game.relics, id],
      marketPurchased: game.floor,
      marketPurchase: { floor: game.floor, id },
    };
  }
  if (game.phase !== 'relic' || !getRelicOptions(game).includes(id))
    return game;
  return prepareShop({
    ...game,
    relics: [...game.relics, id],
    phase: game.floor === CORE_SECTOR ? 'shop' : 'choose',
    relicOffers: null,
  });
};

export const repair = (game) => {
  const cost = getRepairCost(game);
  if (!['choose', 'shop'].includes(game.phase) || game.shards < cost)
    return game;
  return {
    ...game,
    ...applyHealing(game, REPAIR_HEAL),
    shards: game.shards - cost,
    totalShardsAcquired: getTotalShardsAcquired(game),
    repairPurchases: {
      floor: game.floor,
      count: getSectorRepairCount(game) + 1,
    },
  };
};

export const chooseRoom = (game, roomId) => {
  const paidHardRoute =
    game.portalRun &&
    game.portalRoutes?.hard === roomId &&
    !isStarterPortalChamber(game) &&
    !game.portalRoutes.hardUnlocked;
  if (
    game.phase !== 'choose' ||
    roomId === 'boss' ||
    (paidHardRoute
      ? game.portalRoutes.intro ||
        game.floor === MINI_BOSS_SECTOR ||
        game.shards < HARD_PORTAL_COST
      : !getRooms(game).some((room) => room.id === roomId))
  )
    return game;
  return {
    ...game,
    phase: game.breachIntro ? 'ready' : 'guardianReady',
    guardian: game.breachIntro
      ? null
      : createGuardian(
          game,
          createDoor(roomId, game.floor) ||
            getRooms(game).find((room) => room.id === roomId)
        ),
    shards: game.shards - (paidHardRoute ? HARD_PORTAL_COST : 0),
    totalShardsAcquired: getTotalShardsAcquired(game),
    portalRoutes: paidHardRoute
      ? { ...game.portalRoutes, hardUnlocked: true, hardPaid: true }
      : game.portalRoutes,
    boardTileResolution: null,
    powerUp: null,
    bountyTag: { targetIndex: null, attempt: null },
    throwCombo: null,
    phaseAnchor: { ...game.phaseAnchor, armed: false },
    portalPair: generatePortalPairForRoom(game, roomId),
    roomId,
    boardCondition: getRoomBoardCondition(game, roomId),
    roll: null,
    doorBoard: game.breachIntro
      ? generateBreachBoard()
      : generateDoorBoard(Math.random, { floor: game.floor }),
    doorBoardVersion: game.breachIntro
      ? BREACH_BOARD_VERSION
      : DOOR_BOARD_VERSION,
    landingIndex: null,
    boardModifier: 0,
    flipped: false,
    boardTreasureReward: null,
    boardTreasureRewards: [],
    chainReactionActivation: null,
  };
};

export const cancelRoomChoice = (game) => {
  if (game.phase !== 'ready' || game.breachIntro) return game;
  return {
    ...game,
    phase: 'choose',
    boardTileResolution: null,
    powerUp: null,
    bountyTag: { targetIndex: null, attempt: null },
    throwCombo: null,
    phaseAnchor: { ...game.phaseAnchor, armed: false },
    portalPair: createEmptyPortalPair(),
    roomId: null,
    boardCondition: null,
    roll: null,
    doorBoard: null,
    doorBoardVersion: null,
    landingIndex: null,
    boardModifier: 0,
    flipped: false,
    boardTreasureReward: null,
    boardTreasureRewards: [],
    chainReactionActivation: null,
  };
};

export const rollRoom = (game, roomId, naturalRoll) => {
  if (
    game.phase !== 'ready' ||
    game.roomId !== roomId ||
    !getRooms(game).some((room) => room.id === roomId)
  )
    return game;
  if (!isValidDieRoll(naturalRoll, game.selectedDie)) return game;
  return {
    ...startThrowCombo(startBountyTagThrow(game)),
    phase: 'rolled',
    boardTileResolution: null,
    powerUp: null,
    roomId,
    roll: naturalRoll,
    landingIndex: null,
    boardModifier: 0,
    flipped: false,
    boardTreasureReward: null,
    boardTreasureRewards: [],
    chainReactionActivation: null,
    doorBoardVersion: game.breachIntro
      ? BREACH_BOARD_VERSION
      : DOOR_BOARD_VERSION,
  };
};

export const beginRoomRoll = (game, roomId) => {
  if (
    game.phase !== 'ready' ||
    game.roomId !== roomId ||
    !getRooms(game).some((room) => room.id === roomId)
  )
    return game;
  game = migratePlayerBoardBalance(game);
  return {
    ...startThrowCombo(startBountyTagThrow(game)),
    phase: 'rolled',
    boardTileResolution: null,
    powerUp: null,
    roomId,
    roll: null,
    landingIndex: null,
    boardModifier: 0,
    flipped: false,
    boardTreasureReward: null,
    boardTreasureRewards: [],
    chainReactionActivation: null,
    doorBoardVersion: game.breachIntro
      ? BREACH_BOARD_VERSION
      : DOOR_BOARD_VERSION,
  };
};

export const commitRoomRoll = (
  game,
  naturalRoll,
  landingIndex = null,
  random = Math.random,
  relaunchId = null,
  faceValues = null
) => {
  if (game.phase === 'rolled' && getBoardRelaunchRequest(game))
    return completeBoardRelaunch(
      game,
      landingIndex,
      relaunchId,
      random,
      faceValues,
      naturalRoll
    );
  if (
    game.phase !== 'rolled' ||
    game.roll !== null ||
    !isValidDieRoll(naturalRoll, game.selectedDie)
  )
    return game;
  const landing = getBoardSquare(game.doorBoard, landingIndex);
  const settled = {
    ...commitChainReaction(game, landingIndex),
    landingIndex: landing ? landingIndex : null,
  };
  return finishBoardTileEffects(
    {
      ...settled,
      roll: naturalRoll,
      phaseAnchor: { ...game.phaseAnchor, armed: false },
      landingIndex: landing ? landingIndex : null,
      boardModifier: landing?.modifier ?? 0,
      flipped: false,
    },
    random
  );
};

// Single-digit rolls are padded before reversing: 1 → 01 → 10.
export const reverseRollDigits = (roll) =>
  Number.isInteger(roll)
    ? Number(String(roll).padStart(2, '0').split('').reverse().join(''))
    : null;

export const needsMirrorActivation = (game) =>
  !getBoardRelaunchRequest(game) &&
  !needsBoardTileChoice(game) &&
  ['rolled', 'bossPlayerRolled', 'bossRolled'].includes(game.phase) &&
  Number.isInteger(game.roll) &&
  game.roll >= 0 &&
  game.roll <= 100 &&
  (game.phase === 'rolled' || Number.isInteger(game.landingIndex)) &&
  game.relics?.includes('mirrorChip') === true &&
  reverseRollDigits(game.roll) > game.roll &&
  !game.floorFlipUsed &&
  !game.flipped &&
  !game.bossRoundState?.attack;

export const flipRoll = (game) =>
  needsMirrorActivation(game)
    ? { ...game, flipped: true, floorFlipUsed: true }
    : game;

export const getBaseRoll = (game) => {
  if (game.roll === null) return null;
  return game.flipped ? reverseRollDigits(game.roll) : game.roll;
};

export const getBoardAdjustedRoll = (game) => {
  if (game.roll === null) return null;
  if (isDieFrozen(game)) return 0;
  const initialRoll = getBaseRoll(game);
  const steps = getBoardRollSteps(game, initialRoll);
  return steps.length
    ? steps[steps.length - 1].value
    : initialRoll + (game.boardModifier || 0);
};

const ROLL_RELIC_PRIORITY = [
  'luckySeven',
  'overclock',
  'glitchBattery',
  'echoCore',
  'vaultKey',
  'prismDrive',
  'coreBreaker',
];

export const getRollRelicEffects = (game) => {
  if (isDieFrozen(game)) return [];
  const boardAdjustedRoll = getBoardAdjustedRoll(game);
  if (boardAdjustedRoll === null) return [];
  const relicGame = { ...game, roll: boardAdjustedRoll };
  const effects = [];
  const orderedIds = [
    ...ROLL_RELIC_PRIORITY,
    ...game.relics.filter((id) => !ROLL_RELIC_PRIORITY.includes(id)),
  ];

  orderedIds.forEach((id) => {
    const current = RELICS[id];
    if (
      !current ||
      !game.relics.includes(id) ||
      !current.rollBonus ||
      !rollConditionMatches(current.rollCondition, relicGame, current)
    )
      return;
    let bonus = current.rollBonus;
    if (current.bonusPerBump)
      bonus *= Math.min(
        3,
        getThrowCombo(game)?.events.filter(({ type }) => type === 'bump')
          .length || 0
      );
    if (current.bonusPerNegativeTile)
      bonus *= Math.min(
        3,
        getTriggeredBoardSquares(game).filter(({ square }) =>
          isNegativeBoardSquare(square)
        ).length
      );
    if (current.portalBonus && rollConditionMatches('portal', relicGame))
      bonus += current.portalBonus;
    if (current.poweredBonus && rollConditionMatches('powered', relicGame))
      bonus += current.poweredBonus;
    if (current.evenBonus && boardAdjustedRoll % 2 === 0)
      bonus += current.evenBonus;
    const message =
      current.id === 'luckySeven'
        ? 'Lucky digit detected: +20'
        : current.id === 'overclock'
        ? 'Power pushed beyond safe limits: +12'
        : current.id === 'glitchBattery'
        ? 'Low roll converted into a surge: +30'
        : current.id === 'echoCore'
        ? `Matching ${formatDieValue(boardAdjustedRoll)} echoed: +18`
        : current.id === 'prismDrive'
        ? 'Even signal refracted: +8'
        : `${current.name} activated: +${bonus}`;
    effects.push({
      id,
      bonus,
      message,
      icon: current.icon,
      name: current.name,
    });
  });

  return effects;
};

export const getEffectiveRoll = (game, roomOverride = null) => {
  const value = getBoardAdjustedRoll(game);
  if (value === null) return null;
  if (isDieFrozen(game)) return 0;
  const relicBonus = getRollRelicEffects(game, roomOverride).reduce(
    (total, effect) => total + effect.bonus,
    0
  );
  return value + relicBonus;
};

export const getLastStandRoom = (game) => ({
  ...getRooms(game).find((room) => room.id === game.roomId),
  name: 'Last Stand Protocol',
  target: LAST_STAND_TARGET,
});

export const resolveLastStandRoll = (game) => {
  const pending = game.lastStandProtocol?.pending;
  let rollGame = getLastStandRollGame(game);
  if (pending?.stage !== 'settled' || !rollGame) return game;
  rollGame = flipRoll(rollGame);
  const room = getLastStandRoom(game);
  const finalRoll = getEffectiveRoll(rollGame, room);
  if (!Number.isFinite(finalRoll)) return game;
  return {
    ...game,
    floorFlipUsed: rollGame.floorFlipUsed,
    lastStandProtocol: {
      used: true,
      pending: null,
      lastAttempt: {
        encounter: pending.encounter,
        context: pending.context,
        rescuedAtImpact: pending.impactSeen === true,
        naturalRoll: pending.roll,
        boardRoll: getBoardAdjustedRoll(rollGame),
        finalRoll,
        success: finalRoll >= LAST_STAND_TARGET,
      },
    },
  };
};

export const resolveRoll = (
  game,
  random = Math.random,
  roomOverride = null
) => {
  if (
    game.phase !== 'rolled' ||
    game.roll === null ||
    getBoardRelaunchRequest(game) ||
    needsBoardTileChoice(game) ||
    game.lastStandProtocol?.pending
  )
    return game;
  game = flipRoll(game);
  const room =
    roomOverride ||
    getRooms(game).find((candidate) => candidate.id === game.roomId);
  if (!room || (room.kind === 'Boss' && !game.guardian?.active)) return game;

  const introThrow = game.breachIntro && game.floor === 1;
  const landing =
    getTriggeredBoardSquares(game).find(
      ({ index }) => index === game.landingIndex
    )?.square || getBoardSquare(game.doorBoard, game.landingIndex);
  const effectiveRoll = game.guardian?.active
    ? Math.max(0, getEffectiveRoll(game, room))
    : getEffectiveRoll(game);
  const success = introThrow || effectiveRoll >= room.target;
  const chainActivation = getChainReactionActivation(game);
  const relicActivations = chainActivation ? [chainActivation] : [];
  const frozen = isDieFrozen(game);
  const enemyAttackCount = success ? 0 : getEnemyAttackCount(game);
  if (frozen)
    relicActivations.push({
      id: 'boardTrap',
      name: 'Trap',
      icon: '!',
      message: introThrow
        ? 'Die frozen for this turn. No enemy is present.'
        : 'Die frozen for this turn. Enemy attacks twice.',
    });
  const rawDamage = success
    ? 0
    : (game.guardian?.active &&
      !game.guardian.isWarden &&
      room.originalDamage === undefined
        ? getGuardianCounterDamage(room)
        : room.damage) *
        enemyAttackCount +
      (game.relics.includes('overclock') ? enemyAttackCount : 0);
  const enemyAttacks = resolveEnemyAttacks({
    damage: enemyAttackCount ? rawDamage / enemyAttackCount : 0,
    count: enemyAttackCount,
    defenses: [
      ...getRelicBlockEffects(game, {
        adjustedRoll: getBoardAdjustedRoll(game),
      }),
      {
        id: 'boardShield',
        amount: getBoardEffectTotal(game, 'shield'),
        once: true,
      },
    ],
  });
  const defenseEffects = enemyAttacks.reductions.filter(
    ({ id }) => id !== 'boardShield'
  );
  const boardShieldBlocked =
    enemyAttacks.reductions.find(({ id }) => id === 'boardShield')?.amount || 0;
  const damage = enemyAttacks.damage;
  relicActivations.push(
    ...defenseEffects.map(({ id, amount }) => ({
      id,
      message: RELICS[id].name + ' blocked ' + amount + ' damage',
    }))
  );
  if (boardShieldBlocked > 0) {
    relicActivations.push({
      id: 'boardShield',
      name: 'Board shield',
      icon: '⬡',
      message: 'Board shield blocked ' + boardShieldBlocked + ' damage',
    });
  }
  if (!success && game.relics.includes('overclock')) {
    relicActivations.push({
      id: 'overclockBacklash',
      name: 'Overclock Backlash',
      icon: '!',
      message: 'Unsafe power added 1 damage',
    });
  }

  const roomHealing = success ? room.heal : 0;
  const healingEffects = getRelicHealingEffects(game, {
    victory: success,
    adjustedRoll: getBoardAdjustedRoll(game),
    attack: effectiveRoll,
    enemyHpBefore: game.guardian?.hp ?? room.target,
  });
  const patchHealing =
    healingEffects.find(({ id }) => id === 'patchKit')?.amount || 0;
  const relicHealing = healingEffects
    .filter(({ id }) => id !== 'patchKit')
    .reduce((sum, { amount }) => sum + amount, 0);
  const boardHealing = getBoardEffectTotal(game, 'heal');
  const healedBeforeCounter = applyHealing(
    game,
    roomHealing + patchHealing + relicHealing + boardHealing
  );
  const regularHp = Math.max(0, healedBeforeCounter.hp - damage);
  const maxHp = healedBeforeCounter.maxHp;
  const boardDamage = getBoardEffectTotal(game, 'damage');
  const hpWithoutProtocol = Math.max(0, regularHp - boardDamage);
  if (hpWithoutProtocol === 0 && canTriggerLastStand(game)) {
    return requestLastStandRoll(
      game,
      'room',
      damage + boardDamage,
      game.hp + roomHealing + patchHealing + relicHealing + boardHealing,
      random
    );
  }
  const protocolSaved = hpWithoutProtocol === 0 && didLastStandSurvive(game);
  const hp = protocolSaved ? 1 : hpWithoutProtocol;
  const protocolPrevented = protocolSaved
    ? Math.max(
        0,
        damage +
          boardDamage -
          Math.max(
            0,
            game.hp +
              roomHealing +
              patchHealing +
              relicHealing +
              boardHealing -
              1
          )
      )
    : 0;
  const survivalRoll =
    game.lastStandProtocol?.lastAttempt?.encounter ===
    getLastStandEncounter(game)
      ? game.lastStandProtocol.lastAttempt
      : null;
  if (survivalRoll) {
    relicActivations.push({
      id: 'lastStandProtocol',
      message: protocolSaved
        ? `Survival roll ${formatDieValue(
            survivalRoll.finalRoll
          )} beat 75. You survived at 1 HP. Protocol consumed.`
        : `Survival roll ${formatDieValue(
            survivalRoll.finalRoll
          )} did not beat 75. Protocol consumed.`,
    });
  }
  relicActivations.push(
    ...healingEffects.map(({ id, message }) => ({ id, message }))
  );
  const shardEffects = getRelicShardEffects(game, {
    victory: success,
    adjustedRoll: getBoardAdjustedRoll(game),
    attack: effectiveRoll,
    enemyHpBefore: game.guardian?.hp ?? room.target,
  });
  const relicShards = shardEffects.reduce((sum, { amount }) => sum + amount, 0);
  const bountyTagOutcome = getBountyTagOutcome(game, success);
  const efficiencyBonus =
    success && game.guardian?.round === 1 && !game.guardian.isWarden
      ? FIRST_STRIKE_SHARDS
      : 0;
  const earned = success
    ? room.shards +
      efficiencyBonus +
      relicShards +
      (bountyTagOutcome?.earned || 0)
    : 0;
  const boardShards = getBoardEffectTotal(game, 'shards');
  const totalEarned = earned + boardShards;
  const shards = game.shards + totalEarned;
  relicActivations.push(
    ...shardEffects.map(({ id, message }) => ({ id, message }))
  );
  if (bountyTagOutcome) relicActivations.push(bountyTagOutcome);
  if (boardShards > 0) {
    relicActivations.push({
      id: 'boardCache',
      name: 'Board cache',
      icon: '✦',
      message: `Board tiles released +${boardShards} shards`,
    });
  }
  const actualBoardHealing = boardHealing;
  if (actualBoardHealing > 0) {
    relicActivations.push({
      id: 'boardHeal',
      name: 'Board pulse',
      icon: '+',
      message: `Board tiles: + ${actualBoardHealing} Max HP`,
    });
  }
  if (boardDamage > 0) {
    relicActivations.push({
      id: 'boardHazard',
      name: 'Board hazard',
      icon: '!',
      message: `Board tiles dealt ${boardDamage} damage`,
    });
  }
  const bonusRelic =
    success && room.bonusRelic
      ? generateRelicOptions(game, random)[0] ||
        (game.guardian?.isWarden ? drawTreasureRelic(game, random).id : null)
      : null;
  const won = game.floor === FLOORS.length && success;
  const lost = hp === 0;
  const victoryScore = won
    ? getVictoryScore({
        hp,
        shards,
        relics: bonusRelic ? [...game.relics, bonusRelic] : game.relics,
        bossRound: game.bossRound,
      })
    : null;
  const finalScore =
    victoryScore ||
    (lost
      ? getDefeatScore({
          floor: game.floor,
          shards,
          relics: bonusRelic ? [...game.relics, bonusRelic] : game.relics,
          enemyMaxHp: game.guardian?.maxHp || room.target,
          enemyHp: Math.max(
            0,
            (game.guardian?.hp ?? room.target) - Math.max(0, effectiveRoll)
          ),
        })
      : null);
  const score = finalScore?.score || 0;
  const scoreBreakdown = finalScore?.scoreBreakdown || [];
  const result = {
    roomId: room.id,
    floor: game.floor,
    roomName: room.name,
    success,
    effectiveRoll,
    target: introThrow ? null : room.target,
    naturalRoll: game.roll,
    damage: damage + boardDamage - protocolPrevented,
    protocolSaved,
    survivalRoll,
    boardShieldBlocked,
    retaliationDamage: rawDamage,
    frozen,
    enemyAttackCount,
    damagePrevention: [
      ...defenseEffects.map(({ id, amount }) => ({
        name: RELICS[id].name + ' blocked',
        amount,
      })),
      { name: 'Board shield blocked', amount: boardShieldBlocked },
    ].filter(({ amount }) => amount > 0),
    healed: roomHealing + patchHealing + relicHealing + boardHealing,
    healedOnLanding: boardHealing,
    maxHpOnLanding: Math.max(0, game.hp + boardHealing - getMaxHp(game)),
    maxHpGained: maxHp - getMaxHp(game),
    earned: totalEarned,
    boardLanding: landing ? { ...landing, index: game.landingIndex } : null,
    boardTreasureReward: game.boardTreasureReward || null,
    boardTreasureRewards: getBoardTreasureRewards(game),
    chainReactionActivation: game.chainReactionActivation,
    bountyTag: bountyTagOutcome,
    throwCombo: game.throwCombo,
    boardDamage,
    bonusRelic,
    efficiencyBonus,
    relicActivations,
    scoreBreakdown,
    enemy: getSectorMinion(game.floor),
    training: introThrow,
    loot: {
      shards: introThrow ? totalEarned : earned,
      heal: introThrow
        ? boardHealing
        : roomHealing + patchHealing + relicHealing,
      relic: introThrow
        ? getBoardTreasureRewards(game)[0]?.id || null
        : bonusRelic,
    },
  };

  return {
    ...consumeRelicHealingEffects(game, healingEffects),
    phase: won ? 'won' : lost ? 'lost' : 'result',
    hp,
    maxHp,
    shards,
    totalShardsAcquired: acquireShards(game, totalEarned),
    relics:
      bonusRelic && !game.relics.includes(bonusRelic)
        ? [...game.relics, bonusRelic]
        : game.relics,
    score,
    bestScore: won || lost ? Math.max(game.bestScore, score) : game.bestScore,
    newBestScore: (won || lost) && score > game.bestScore,
    victories: game.victories + (won ? 1 : 0),
    lastResult: result,
    history: [result, ...game.history].slice(0, 5),
  };
};

export const continueRun = (game) => {
  if (game.phase !== 'result') return game;
  if (
    game.floor === MINI_BOSS_SECTOR &&
    !game.miniBoss?.defeated &&
    !game.guardian?.defeated
  )
    return beginSectorMiniBoss(game);
  const nextFloor = Math.min(TOTAL_SECTORS, game.floor + 1);
  const offerRelic = !game.portalRun && [2, 4, 6, 8].includes(game.floor);
  const nextGame = {
    ...game,
    guardian: null,
    boardTileResolution: null,
    floor: nextFloor,
    bountyTag: { targetIndex: null, attempt: null },
    throwCombo: null,
    phaseAnchor: { ...game.phaseAnchor, armed: false },
    portalPair: createEmptyPortalPair(),
    phase: nextFloor === CORE_SECTOR ? 'shop' : offerRelic ? 'relic' : 'choose',
    breachIntro: false,
    marketPurchased: null,
    marketPurchase: null,
    roomId: null,
    roll: null,
    flipped: false,
    floorFlipUsed: nextFloor === game.floor ? game.floorFlipUsed : false,
    boardCondition: null,
    lastResult: null,
    relicOffers: null,
    doorBoard: null,
    doorBoardVersion: null,
    landingIndex: null,
    boardModifier: 0,
    boardTreasureReward: null,
    boardTreasureRewards: [],
    chainReactionActivation: null,
  };
  if (nextFloor === CORE_SECTOR) return prepareShop(nextGame);
  if (game.portalRun) {
    const portalRoutes = generatePortalRoutes(game, nextFloor);
    return {
      ...nextGame,
      portalRoutes,
      doorOffers: { ...game.doorOffers, [nextFloor]: portalRoutes.normal },
      relicOffers: [2, 4, 6, 8].includes(nextFloor)
        ? generateMarketOptions(nextGame)
        : null,
    };
  }
  return offerRelic
    ? { ...nextGame, relicOffers: generateRelicOptions(nextGame) }
    : nextGame;
};

// The midpoint fight shares the normal throw pipeline. Its encounter state
// stays separate from the Core and from the Sector 5 result used for routing.
const beginSectorMiniBoss = (game, random = Math.random) => {
  if (
    game.phase !== 'result' ||
    game.floor !== MINI_BOSS_SECTOR ||
    game.miniBoss?.defeated ||
    game.hp <= 0
  )
    return game;
  return {
    ...game,
    phase: 'minibossReady',
    roll: null,
    flipped: false,
    landingIndex: null,
    boardModifier: 0,
    boardTileResolution: null,
    boardTreasureReward: null,
    boardTreasureRewards: [],
    powerUp: null,
    chainReactionActivation: null,
    throwCombo: null,
    bountyTag: { targetIndex: null, attempt: null },
    portalPair: createEmptyPortalPair(),
    doorBoard: generateDoorBoard(random, { floor: game.floor }),
    doorBoardVersion: DOOR_BOARD_VERSION,
    miniBoss: {
      active: true,
      hp: MINI_BOSS_HP,
      round: 1,
      defeated: false,
      rewarded: false,
      roomResult: game.lastResult,
      lastRound: null,
      offers: [],
      launch: null,
    },
  };
};

const getMiniBossRollGame = (game) => ({
  ...game,
  phase: game.phase === 'minibossReady' ? 'ready' : 'rolled',
});

export const updateMiniBossThrow = (game, update) => {
  if (!['minibossReady', 'minibossRolled'].includes(game.phase)) return game;
  const rollGame = getMiniBossRollGame(game);
  const next = update(rollGame);
  return next === rollGame ? game : { ...next, phase: game.phase };
};

export const beginMiniBossRoll = (game, launch) => {
  if (game.phase !== 'minibossReady') return game;
  const next = beginRoomRoll(getMiniBossRollGame(game), game.roomId);
  if (next.phase !== 'rolled') return game;
  return {
    ...next,
    phase: 'minibossRolled',
    miniBoss: { ...game.miniBoss, launch, lastRound: null },
  };
};

const getMiniBossAttack = (game) =>
  Math.max(
    0,
    getEffectiveRoll(getMiniBossRollGame(game), getMiniBossRoom(game)) || 0
  );

export const resolveMiniBossAttack = (game, random = Math.random) => {
  if (
    game.phase !== 'minibossRolled' ||
    !Number.isInteger(game.roll) ||
    !Number.isInteger(game.landingIndex) ||
    needsBoardTileChoice(game) ||
    getBoardRelaunchRequest(game) ||
    game.lastStandProtocol?.pending
  )
    return game;
  game = updateMiniBossThrow(game, flipRoll);
  const attack = getMiniBossAttack(game);
  const before = game.miniBoss.hp;
  const enemyHpAfter = Math.max(0, before - attack);
  const won = enemyHpAfter === 0;
  const intent = getMiniBossIntent(game);
  const rawDamage = won
    ? 0
    : intent.damage + (game.relics.includes('overclock') ? 1 : 0);
  const relicBlock = getRelicBlockEffects(game, {
    adjustedRoll: getBoardAdjustedRoll(game),
  }).reduce((sum, { amount }) => sum + amount, 0);
  const shieldBlocked = Math.min(
    Math.max(0, rawDamage - relicBlock),
    getBoardEffectTotal(game, 'shield')
  );
  const boardDamage = getBoardEffectTotal(game, 'damage');
  const damage =
    Math.max(0, rawDamage - relicBlock - shieldBlocked) + boardDamage;
  const healingEffects = getRelicHealingEffects(game, {
    victory: won,
    adjustedRoll: getBoardAdjustedRoll(game),
    attack,
    enemyHpBefore: before,
  });
  const healed =
    getBoardEffectTotal(game, 'heal') +
    healingEffects.reduce((sum, { amount }) => sum + amount, 0);
  const health = applyHealing(game, healed);
  const hpWithoutProtocol = Math.max(0, health.hp - damage);
  if (hpWithoutProtocol === 0 && canTriggerLastStand(game))
    return requestLastStandRoll(game, 'miniboss', damage, health.hp, random);
  const protocolSaved = hpWithoutProtocol === 0 && didLastStandSurvive(game);
  const hp = protocolSaved ? 1 : hpWithoutProtocol;
  const lost = hp === 0;
  const boardShards = getBoardEffectTotal(game, 'shards');
  const bounty = getBountyTagOutcome(game, won && !lost);
  const shardEffects = getRelicShardEffects(game, {
    victory: won && !lost,
    adjustedRoll: getBoardAdjustedRoll(game),
    attack,
    enemyHpBefore: before,
  });
  const earned =
    boardShards +
    (bounty?.earned || 0) +
    (won && !lost ? MINI_BOSS_SHARDS : 0) +
    shardEffects.reduce((sum, { amount }) => sum + amount, 0);
  const offers =
    won && !lost ? generateRelicOptions(game, random).slice(0, 3) : [];
  const defeatScore = lost
    ? getDefeatScore({
        floor: game.floor,
        shards: game.shards + earned,
        relics: game.relics,
        enemyMaxHp: MINI_BOSS_HP,
        enemyHp: enemyHpAfter,
      })
    : null;
  const consumed = getTriggeredBoardSquares(game)
    .filter(({ square }) =>
      ['heal', 'shards', 'relic'].includes(square.effect?.type)
    )
    .map(({ index }) => index);
  const lastRound = {
    miniBoss: true,
    roomId: RIFT_WARDEN.id,
    floor: game.floor,
    round: game.miniBoss.round,
    playerHpBefore: game.hp,
    playerMaxHpBefore: getMaxHp(game),
    roomName: RIFT_WARDEN.name,
    enemy: RIFT_WARDEN,
    target: before,
    effectiveRoll: attack,
    attackDealt: Math.min(before, attack),
    enemyHpAfter,
    success: won && !lost,
    damage: protocolSaved
      ? Math.min(damage, Math.max(0, health.hp - 1))
      : damage,
    healed,
    shieldBlocked,
    healedOnLanding: getBoardEffectTotal(game, 'heal'),
    maxHpOnLanding: Math.max(
      0,
      game.hp + getBoardEffectTotal(game, 'heal') - getMaxHp(game)
    ),
    earned,
    protocolSaved,
    intent: intent.name,
    scoreBreakdown: defeatScore?.scoreBreakdown || [],
    loot: {
      shards: won && !lost ? MINI_BOSS_SHARDS : 0,
      heal: 0,
      relicChoice: offers.length > 0,
    },
  };
  return {
    ...consumeRelicHealingEffects(game, healingEffects),
    hp,
    maxHp: health.maxHp,
    shards: game.shards + earned,
    totalShardsAcquired: acquireShards(game, earned),
    phase: lost ? 'lost' : won ? 'minibossReward' : 'minibossResult',
    lastResult: lost ? lastRound : game.lastResult,
    miniBoss: {
      ...game.miniBoss,
      hp: enemyHpAfter,
      active: !won && !lost,
      defeated: won && !lost,
      lastRound,
      offers,
      consumed: [...new Set([...(game.miniBoss.consumed || []), ...consumed])],
    },
    history: lost ? [lastRound, ...game.history].slice(0, 5) : game.history,
    score: lost ? defeatScore.score : game.score,
    ...(lost
      ? {
          bestScore: Math.max(game.bestScore, defeatScore.score),
          newBestScore: defeatScore.score > game.bestScore,
        }
      : {}),
  };
};

export const nextMiniBossRound = (game) => {
  if (game.phase !== 'minibossResult' || game.miniBoss.hp <= 0) return game;
  return {
    ...game,
    phase: 'minibossReady',
    roll: null,
    flipped: false,
    landingIndex: null,
    boardModifier: 0,
    boardTileResolution: null,
    powerUp: null,
    boardTreasureReward: null,
    boardTreasureRewards: [],
    chainReactionActivation: null,
    throwCombo: null,
    bountyTag: { targetIndex: null, attempt: null },
    portalPair: createEmptyPortalPair(),
    doorBoard: game.doorBoard.map((square, index) =>
      (game.miniBoss.consumed || []).includes(index) &&
      ['heal', 'shards', 'relic'].includes(square.effect?.type)
        ? { modifier: 20 }
        : square
    ),
    miniBoss: {
      ...game.miniBoss,
      round: game.miniBoss.round + 1,
      launch: null,
    },
  };
};
